import { useState } from "react";
import { Users, Trash2, RotateCcw, Wand2, AlertTriangle, PlusCircle, Loader2, Sparkles, Wrench, UserPlus, Target } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AutoResizeTextarea from "@/components/AutoResizeTextarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  BACKEND_URL, uid, CAUTIONARY_TALES,
  COVERED_OPTS, CREDIBILITY_OPTS, GAP_TYPES, PRIORITY_OPTS,
  FIX_TYPES, TIMING_OPTS, COMMITMENT_OPTS, SEVERITY_STYLES,
} from "./constants";

// ---------- Cautionary tale (right column) ----------
function CautionaryTale({ tale, testid }) {
  return (
    <Card className="border-amber-200 bg-amber-50 sticky top-4" data-testid={testid}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-amber-900 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Cautionary tale · {tale.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-xs text-amber-900">
        <p>{tale.body}</p>
        <p className="font-semibold">Lesson: <span className="font-normal">{tale.lesson}</span></p>
      </CardContent>
    </Card>
  );
}

// ---------- Guidance / coach warnings ----------
function GuidancePanel({ warnings, onDismiss, onRefresh, busy, testid }) {
  if (!warnings || !warnings.length) return null;
  return (
    <Card className="border-teal-200 bg-teal-50/50" data-testid={testid}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-teal-900 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Strategy coach warnings
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={onRefresh} disabled={busy} className="h-7 px-2 text-teal-700">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            <span className="ml-1 text-xs">Re-check</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {warnings.map((w) => (
          <div key={w.id} className={`text-xs border px-2 py-1.5 rounded flex items-start gap-2 ${SEVERITY_STYLES[w.severity] || SEVERITY_STYLES.medium}`}>
            <Badge variant="outline" className="text-[10px] capitalize shrink-0">{w.severity}</Badge>
            <span className="flex-1 leading-snug">{w.message}</span>
            <button onClick={() => onDismiss(w.id)} className="opacity-60 hover:opacity-100">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ---------- A · Team composition ----------
function TeamComposition({ data, setData, persistTab }) {
  const [aiBusy, setAiBusy] = useState(false);
  const members = data.team_members || [];
  const setMembers = (n) => { const next = { ...data, team_members: n }; setData(next); persistTab(next); };
  const setMembersNoPersist = (n) => setData({ ...data, team_members: n });
  const update = (id, patch) => setMembersNoPersist(members.map((m) => m.id === id ? { ...m, ...patch } : m));
  const commit = () => setMembers(members);
  const commitAfter = (fn) => { fn(); setTimeout(commit, 0); };
  const add = () => setMembers([...members, { id: uid(), name: "", role: "", expertise: "", commitment: "" }]);
  const remove = (id) => setMembers(members.filter((m) => m.id !== id));

  const draftFromDeck = async () => {
    setAiBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/milestones/suggest-team-from-deck`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ existing: members }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.members?.length) {
          setMembers([...members, ...d.members]);
          toast.success(`Added ${d.members.length} team member${d.members.length === 1 ? "" : "s"} from deck`);
        } else if (d.error) {
          toast.info(d.error);
        } else {
          toast.info("No team members found in the deck");
        }
      } else {
        toast.error("Failed");
      }
    } catch (e) { toast.error("Failed"); }
    setAiBusy(false);
  };

  return (
    <Card data-testid="eg-team-composition">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-slate-800 flex items-center gap-2">
            <Users className="w-4 h-4 text-teal-600" /> A · Team composition
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={draftFromDeck} disabled={aiBusy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid="eg-team-ai-draft">
              {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
              <span className="ml-1 text-xs">AI draft from deck</span>
            </Button>
            <Button size="sm" variant="ghost" onClick={add} className="h-7 px-2 text-teal-600" data-testid="eg-team-add">
              <UserPlus className="w-4 h-4 mr-1" /> Add person
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-500">
          Who is on the team today — founders, employees, key advisors, board members. Be honest about commitment level.
        </p>
      </CardHeader>
      <CardContent>
        {members.length === 0 && (
          <p className="text-xs text-slate-400 italic px-3 py-6 border rounded-lg text-center">
            No team members yet. Click &quot;Add person&quot; to start.
          </p>
        )}
        {members.length > 0 && (
          <div className="border rounded-lg overflow-x-auto">
            <div className="min-w-[900px]">
              <div className="grid gap-0 bg-slate-50 border-b text-[10px] font-medium text-slate-500 uppercase tracking-wide" style={{ gridTemplateColumns: "1.5fr 1.6fr 2.8fr 1.6fr 0.5fr" }}>
                <div className="px-2 py-2 border-r">Name</div>
                <div className="px-2 py-2 border-r">Role / title</div>
                <div className="px-2 py-2 border-r">Expertise & prior experience</div>
                <div className="px-2 py-2 border-r">Commitment</div>
                <div className="px-2 py-2"></div>
              </div>
              {members.map((m) => (
                <div key={m.id} className="grid gap-0 border-b last:border-b-0 hover:bg-slate-50/40" style={{ gridTemplateColumns: "1.5fr 1.6fr 2.8fr 1.6fr 0.5fr" }} data-testid={`eg-team-row-${m.id}`}>
                  <div className="border-r p-1">
                    <Input value={m.name} onChange={(e) => update(m.id, { name: e.target.value })} onBlur={commit} className="h-8 text-xs border-0 focus-visible:ring-1" placeholder="e.g. Dr Jane Smith" data-testid={`eg-team-name-${m.id}`} />
                  </div>
                  <div className="border-r p-1">
                    <Input value={m.role} onChange={(e) => update(m.id, { role: e.target.value })} onBlur={commit} className="h-8 text-xs border-0 focus-visible:ring-1" placeholder="e.g. CSO, CMC lead" data-testid={`eg-team-role-${m.id}`} />
                  </div>
                  <div className="border-r p-1">
                    <AutoResizeTextarea value={m.expertise} onChange={(e) => update(m.id, { expertise: e.target.value })} onBlur={commit} minRows={1} maxHeight={200} className="text-xs border-0 focus-visible:ring-1 py-1.5" placeholder="e.g. 10y AAV process development, ex-Spark; 2 INDs cleared" data-testid={`eg-team-exp-${m.id}`} />
                  </div>
                  <div className="border-r p-1">
                    <Select value={m.commitment || ""} onValueChange={(v) => commitAfter(() => update(m.id, { commitment: v }))}>
                      <SelectTrigger className="h-8 text-[11px] border-0 focus-visible:ring-1" data-testid={`eg-team-com-${m.id}`}>
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>{COMMITMENT_OPTS.map((o) => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-start justify-center pt-2">
                    <button onClick={() => remove(m.id)} className="text-slate-300 hover:text-red-500" data-testid={`eg-team-del-${m.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- B · Gap assessment vs milestones ----------
function GapAssessment({ data, viData, setData, persistTab }) {
  const [busy, setBusy] = useState(false);
  const rows = data.capability_map || [];
  const milestones = viData?.milestones || [];
  const setRows = (n) => { const next = { ...data, capability_map: n }; setData(next); persistTab(next); };
  const setRowsNoPersist = (n) => setData({ ...data, capability_map: n });
  const update = (id, patch) => setRowsNoPersist(rows.map((r) => r.id === id ? { ...r, ...patch } : r));
  const commit = () => setRows(rows);
  const commitAfter = (fn) => { fn(); setTimeout(commit, 0); };
  const add = () => setRows([...rows, { id: `cap_${Date.now()}`, capability: "", milestone: "", covered_now: "", who_covers: "", credibility: "", gap_type: "", fix: "", priority: "" }]);
  const remove = (id) => setRows(rows.filter((r) => r.id !== id));

  const aiAssess = async () => {
    if (milestones.length === 0) {
      toast.error("Add milestones on the Milestones tab first — the AI needs them to assess gaps.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/milestones/suggest-table`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          table: "capability_map",
          rows: rows.length ? rows : [],
          context: {
            milestones: milestones.map((m) => ({ milestone: m.milestone, evidence: m.evidence, timeframe: m.timeframe, category: m.category })),
            team_members: (data.team_members || []).map((m) => ({ name: m.name, role: m.role, expertise: m.expertise, commitment: m.commitment })),
            next_inflection: viData?.next_inflection,
          },
        }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.rows?.length) { setRows(d.rows); toast.success(`Assessed ${d.rows.length} capability-milestone gaps`); }
        else toast.info("No gaps identified");
      } else toast.error("Failed");
    } catch (e) { toast.error("Failed"); }
    setBusy(false);
  };

  const credColor = (v) => v === "Strong" ? "bg-emerald-50 text-emerald-800" : v === "Adequate" ? "bg-amber-50 text-amber-800" : v === "Weak" ? "bg-red-50 text-red-800" : "";

  return (
    <Card data-testid="eg-gap-assessment">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-slate-800 flex items-center gap-2">
            <Target className="w-4 h-4 text-teal-600" /> B · Capability gaps vs milestones
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={aiAssess} disabled={busy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid="eg-assess-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
              <span className="ml-1 text-xs">AI assess vs milestones</span>
            </Button>
            <Button size="sm" variant="ghost" onClick={add} className="h-7 px-2 text-teal-600" data-testid="eg-cap-add">
              <PlusCircle className="w-4 h-4 mr-1" /> Add row
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-500">
          For each milestone, what capability is needed and whether the current team can credibly deliver it.
          {milestones.length === 0 && <span className="text-amber-600 font-medium"> Add milestones on the Milestones tab first.</span>}
        </p>
      </CardHeader>
      <CardContent>
        {rows.length === 0 && (
          <p className="text-xs text-slate-400 italic px-3 py-6 border rounded-lg text-center">
            No gaps yet. Click &quot;AI assess vs milestones&quot; (needs milestones + team composition).
          </p>
        )}
        {rows.length > 0 && (
          <div className="border rounded-lg overflow-x-auto">
            <div className="min-w-[1200px]">
              <div className="grid gap-0 bg-slate-50 border-b text-[10px] font-medium text-slate-500 uppercase tracking-wide" style={{ gridTemplateColumns: "2fr 2.4fr 1.2fr 1.5fr 1.3fr 1.6fr 2fr 1.6fr 0.4fr" }}>
                <div className="px-2 py-2 border-r">Capability</div>
                <div className="px-2 py-2 border-r">For which milestone</div>
                <div className="px-2 py-2 border-r">Covered now?</div>
                <div className="px-2 py-2 border-r">Who covers</div>
                <div className="px-2 py-2 border-r">Credibility</div>
                <div className="px-2 py-2 border-r">Gap type</div>
                <div className="px-2 py-2 border-r">First move</div>
                <div className="px-2 py-2 border-r">Priority</div>
                <div className="px-2 py-2"></div>
              </div>
              {rows.map((row) => (
                <div key={row.id} className="grid gap-0 border-b last:border-b-0 hover:bg-slate-50/40" style={{ gridTemplateColumns: "2fr 2.4fr 1.2fr 1.5fr 1.3fr 1.6fr 2fr 1.6fr 0.4fr" }} data-testid={`eg-cap-row-${row.id}`}>
                  <div className="border-r p-1">
                    <AutoResizeTextarea value={row.capability} onChange={(e) => update(row.id, { capability: e.target.value })} onBlur={commit} minRows={1} maxHeight={200} className="text-xs border-0 focus-visible:ring-1 py-1.5" placeholder="e.g. AAV process development" data-testid={`eg-cap-name-${row.id}`} />
                  </div>
                  <div className="border-r p-1">
                    <AutoResizeTextarea value={row.milestone} onChange={(e) => update(row.id, { milestone: e.target.value })} onBlur={commit} minRows={1} maxHeight={200} className="text-xs border-0 focus-visible:ring-1 py-1.5" placeholder="Milestone name" data-testid={`eg-cap-ms-${row.id}`} />
                  </div>
                  <div className="border-r p-1">
                    <Select value={row.covered_now || ""} onValueChange={(v) => commitAfter(() => update(row.id, { covered_now: v }))}>
                      <SelectTrigger className="h-8 text-[11px] border-0 focus-visible:ring-1" data-testid={`eg-cap-cov-${row.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{COVERED_OPTS.map((o) => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="border-r p-1">
                    <Input value={row.who_covers} onChange={(e) => update(row.id, { who_covers: e.target.value })} onBlur={commit} className="h-8 text-xs border-0 focus-visible:ring-1" placeholder="Name / vacant" data-testid={`eg-cap-who-${row.id}`} />
                  </div>
                  <div className={`border-r p-1 ${credColor(row.credibility)}`}>
                    <Select value={row.credibility || ""} onValueChange={(v) => commitAfter(() => update(row.id, { credibility: v }))}>
                      <SelectTrigger className="h-8 text-[11px] border-0 focus-visible:ring-1" data-testid={`eg-cap-cred-${row.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{CREDIBILITY_OPTS.map((o) => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="border-r p-1">
                    <Select value={row.gap_type || ""} onValueChange={(v) => commitAfter(() => update(row.id, { gap_type: v }))}>
                      <SelectTrigger className="h-8 text-[11px] border-0 focus-visible:ring-1" data-testid={`eg-cap-gap-${row.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{GAP_TYPES.map((o) => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="border-r p-1">
                    <AutoResizeTextarea value={row.fix} onChange={(e) => update(row.id, { fix: e.target.value })} onBlur={commit} minRows={1} maxHeight={200} className="text-xs border-0 focus-visible:ring-1 py-1.5" placeholder="Concrete first move" data-testid={`eg-cap-fix-${row.id}`} />
                  </div>
                  <div className="border-r p-1">
                    <Select value={row.priority || ""} onValueChange={(v) => commitAfter(() => update(row.id, { priority: v }))}>
                      <SelectTrigger className="h-8 text-[11px] border-0 focus-visible:ring-1" data-testid={`eg-cap-pri-${row.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{PRIORITY_OPTS.map((o) => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-start justify-center pt-2">
                    <button onClick={() => remove(row.id)} className="text-slate-300 hover:text-red-500" data-testid={`eg-cap-del-${row.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- C · Gap-fix plan (per gap, with AI suggest for how + when) ----------
function GapFixPlan({ data, viData, setData, persistTab }) {
  const [busy, setBusy] = useState(false);
  const [perGapBusy, setPerGapBusy] = useState(null); // row.id currently being AI-suggested
  const rows = data.top_gaps || [];
  const setRows = (n) => { const next = { ...data, top_gaps: n }; setData(next); persistTab(next); };
  const setRowsNoPersist = (n) => setData({ ...data, top_gaps: n });
  const update = (id, patch) => setRowsNoPersist(rows.map((r) => r.id === id ? { ...r, ...patch } : r));
  const commit = () => setRows(rows);
  const commitAfter = (fn) => { fn(); setTimeout(commit, 0); };
  const add = () => setRows([...rows, { id: `tg_${Date.now()}`, gap: "", fix_type: "", timing: "", cost: "", help_from: "", ai_suggestion: "" }]);
  const remove = (id) => setRows(rows.filter((r) => r.id !== id));

  const aiExtract = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/milestones/suggest-table`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          table: "top_gaps",
          rows: rows.length ? rows : [],
          context: {
            capability_map: data.capability_map || [],
            team_members: data.team_members || [],
            milestones: (viData?.milestones || []).map((m) => ({ milestone: m.milestone, timeframe: m.timeframe })),
          },
        }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.rows?.length) { setRows(d.rows); toast.success(`${d.rows.length} priority gaps extracted`); }
        else toast.info("No priority gaps");
      } else toast.error("Failed");
    } catch (e) { toast.error("Failed"); }
    setBusy(false);
  };

  const aiSuggestFix = async (row) => {
    if (!row.gap || !row.gap.trim()) {
      toast.error("Describe the gap first.");
      return;
    }
    setPerGapBusy(row.id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/milestones/suggest-field`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          tab: "execution_gaps",
          field: "gap_fix",
          fieldLabel: "How to fix this specific team gap and when",
          current: row.ai_suggestion || "",
          context: {
            gap: row.gap,
            current_fix_type: row.fix_type,
            current_timing: row.timing,
            team_members: data.team_members || [],
            related_milestones: (viData?.milestones || []).map((m) => ({ milestone: m.milestone, timeframe: m.timeframe })),
          },
        }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.answer) {
          update(row.id, { ai_suggestion: d.answer });
          setTimeout(commit, 0);
          toast.success("AI suggestion ready");
        } else toast.info("No suggestion");
      } else toast.error("Failed");
    } catch (e) { toast.error("Failed"); }
    setPerGapBusy(null);
  };

  return (
    <Card data-testid="eg-gap-fix">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-slate-800 flex items-center gap-2">
            <Wrench className="w-4 h-4 text-teal-600" /> C · Gap-fix plan
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={aiExtract} disabled={busy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid="eg-tg-ai-fill">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
              <span className="ml-1 text-xs">Extract top gaps</span>
            </Button>
            <Button size="sm" variant="ghost" onClick={add} className="h-7 px-2 text-teal-600" data-testid="eg-tg-add">
              <PlusCircle className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-500">
          Priority gaps and how you plan to close each one — including timing. Use &quot;Suggest fix&quot; per gap for concrete next moves.
        </p>
      </CardHeader>
      <CardContent>
        {rows.length === 0 && (
          <p className="text-xs text-slate-400 italic px-3 py-6 border rounded-lg text-center">
            No gaps yet. Click &quot;Extract top gaps&quot; (uses your capability-gap assessment).
          </p>
        )}
        <div className="space-y-2">
          {rows.map((row) => (
            <Card key={row.id} className="border-l-4 border-l-red-400" data-testid={`eg-tg-card-${row.id}`}>
              <CardContent className="pt-3 pb-3 space-y-2">
                <div className="flex items-start gap-2">
                  <AutoResizeTextarea
                    value={row.gap}
                    onChange={(e) => update(row.id, { gap: e.target.value })}
                    onBlur={commit}
                    minRows={1}
                    maxHeight={300}
                    className="flex-1 text-xs"
                    placeholder="Describe the gap (which capability is missing for which milestone)…"
                    data-testid={`eg-tg-gap-${row.id}`}
                  />
                  <button onClick={() => remove(row.id)} className="text-slate-300 hover:text-red-500 mt-1.5" data-testid={`eg-tg-del-${row.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <div>
                    <Label className="text-[10px] text-slate-500 uppercase tracking-wide">How to fix</Label>
                    <Select value={row.fix_type || ""} onValueChange={(v) => commitAfter(() => update(row.id, { fix_type: v }))}>
                      <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`eg-tg-fix-${row.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{FIX_TYPES.map((o) => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500 uppercase tracking-wide">When</Label>
                    <Select value={row.timing || ""} onValueChange={(v) => commitAfter(() => update(row.id, { timing: v }))}>
                      <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`eg-tg-time-${row.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{TIMING_OPTS.map((o) => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Cost</Label>
                    <Input value={row.cost} onChange={(e) => update(row.id, { cost: e.target.value })} onBlur={commit} className="h-8 text-xs mt-0.5" placeholder="e.g. £120k / yr" data-testid={`eg-tg-cost-${row.id}`} />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Help from</Label>
                    <Input value={row.help_from} onChange={(e) => update(row.id, { help_from: e.target.value })} onBlur={commit} className="h-8 text-xs mt-0.5" placeholder="Who can help find them" data-testid={`eg-tg-help-${row.id}`} />
                  </div>
                </div>
                <div className="border-t pt-2">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-[10px] text-slate-500 uppercase tracking-wide">AI suggestion — how &amp; when to fix</Label>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => aiSuggestFix(row)}
                      disabled={perGapBusy === row.id}
                      className="h-6 px-2 text-amber-600 hover:bg-amber-50"
                      data-testid={`eg-tg-suggest-${row.id}`}
                    >
                      {perGapBusy === row.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                      <span className="ml-1 text-xs">Suggest fix</span>
                    </Button>
                  </div>
                  <AutoResizeTextarea
                    value={row.ai_suggestion || ""}
                    onChange={(e) => update(row.id, { ai_suggestion: e.target.value })}
                    onBlur={commit}
                    minRows={1}
                    maxHeight={360}
                    className="text-xs leading-relaxed bg-amber-50/40"
                    placeholder="Click Suggest fix for AI-drafted concrete next moves and timing…"
                    data-testid={`eg-tg-ai-${row.id}`}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------- Main tab ----------
export default function ExecutionGapsTab({ data, setData, persist, viData }) {
  const persistTab = (nextEg) => persist({ execution_gaps: nextEg });
  const [gBusy, setGBusy] = useState(false);

  const checkGuidance = async () => {
    setGBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/milestones/check-guidance`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ tab: "execution_gaps", tab_data: data }),
      });
      if (res.ok) {
        const d = await res.json();
        const next = { ...data, guidance: d.warnings || [] };
        setData(next); persistTab(next);
        if (d.warnings?.length) toast.success(`${d.warnings.length} coach warnings`);
        else toast.info("No warnings");
      } else toast.error("Failed");
    } catch (e) { toast.error("Failed"); }
    setGBusy(false);
  };
  const dismissWarn = (id) => {
    const next = { ...data, guidance: (data.guidance || []).filter((w) => w.id !== id) };
    setData(next); persistTab(next);
  };
  const resetTab = () => {
    if (window.confirm("Reset the Team tab? This clears team members, gap assessment and fix plan.")) {
      const empty = { team_members: [], capability_map: [], red_flags: data.red_flags || [], green_flags: data.green_flags || [], top_gaps: [], key_questions: [], guidance: [] };
      setData(empty); persistTab(empty);
      toast.success("Reset");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="execution-gaps-tab">
      <div className="lg:col-span-2 space-y-4">
        <GuidancePanel warnings={data.guidance || []} onDismiss={dismissWarn} onRefresh={checkGuidance} busy={gBusy} testid="eg-guidance" />
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={checkGuidance} disabled={gBusy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid="eg-guidance-btn">
            {gBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            <span className="ml-1 text-xs">Coach check</span>
          </Button>
          <Button size="sm" variant="ghost" onClick={resetTab} className="h-7 px-2 text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid="eg-reset-btn">
            <RotateCcw className="w-3.5 h-3.5" />
          </Button>
        </div>
        <TeamComposition data={data} setData={setData} persistTab={persistTab} />
        <GapAssessment data={data} viData={viData} setData={setData} persistTab={persistTab} />
        <GapFixPlan data={data} viData={viData} setData={setData} persistTab={persistTab} />
      </div>
      <div className="lg:col-span-1">
        <CautionaryTale tale={CAUTIONARY_TALES.execution_gaps} testid="eg-cautionary" />
      </div>
    </div>
  );
}
